import Head from "next/head";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";

/**
 * Meta case study — "we pointed an AI agent at our own CRM and let it run
 * growth." Every number here is REAL and comes from our prod DB / growth log.
 * That includes the unflattering ones (0 paying customers). Concede reality —
 * an honest, citable metric story is the whole point (GEO signal + credibility).
 *
 * To refresh the numbers: update METRICS below with the latest growth-digest
 * figures and bump `asOf`. Keep it honest — no rounding up, no invented deltas.
 */

const METRICS = {
  asOf: "14 August 2026",
  daysRunning: 26, // autonomous growth engine started 2026-07-19
  pagesIndexed: 84, // live sitemap.xml URL count (83 + /changelog, shipping with it)
  freeTools: 3, // funnel generator, email link checker, cost calculator
  templates: 26,
  glossaryTerms: 24,
  /**
   * Funnel numbers come from the prod DB via the growth digest, which has been
   * blocked from reading it on several runs. They carry their own date rather
   * than borrowing `asOf` — a shipped-pages count and a signup count measured
   * two weeks apart should not be presented as one snapshot.
   */
  funnelAsOf: "13 August 2026",
  /**
   * VISITOR-generated maps only. The prod table holds 37 rows, but 21 of them
   * are the daily health check generating a map against our own API. Counting
   * those would be inventing traffic. All 16 real ones are from launch-day
   * testing on 19–20 July; nothing organic since.
   */
  generatedMaps: 16,
  claimedMaps: 0,
  signups: 7,
  companies: 3,
  payingCustomers: 0,
  mrr: 0,
};

const SHIPPED: { label: string; href: string; note: string }[] = [
  {
    label: "AI Funnel Map Generator",
    href: "/tools/funnel-generator",
    note: "describe your business → a real, claimable funnel map",
  },
  {
    label: "Email Link Checker",
    href: "/tools/email-link-checker",
    note: "SSRF-guarded link health report for any campaign",
  },
  {
    label: "Automation Cost Calculator",
    href: "/tools/cost-calculator",
    note: "your stack's cost vs a flat plan, with shareable results",
  },
  {
    label: "Honest comparison pages",
    href: "/vs/gohighlevel",
    note: "vs GoHighLevel, ActiveCampaign & Make — conceding what they do better",
  },
  {
    label: "Audience pages",
    href: "/for/agencies",
    note: "for agencies, coaches, ecommerce, SaaS, courses & local services",
  },
  {
    label: "Integrations hub",
    href: "/integrations",
    note: "setup guides for Mailgun, SES, SendGrid, Postmark, Brevo, Twilio & webhooks",
  },
  {
    label: "Template gallery",
    href: "/templates",
    note: `${METRICS.templates} clone-in-one-click funnel templates, each its own landing page`,
  },
  {
    label: "MCP server + docs",
    href: "/ai",
    note: "published to the MCP Registry so Claude & ChatGPT can build flows",
  },
  {
    label: "Engineering blog",
    href: "/blog",
    note: "deep write-ups on the MCP server and the map⟷automation sync",
  },
  {
    label: "Automation glossary",
    href: "/learn",
    note: `${METRICS.glossaryTerms} plain-English definitions, cross-linked with every template`,
  },
  {
    label: "Changelog",
    href: "/changelog",
    note: "every production release, generated from git — nobody writes the entries",
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "What is FrazzleCRM's build-in-public experiment?",
    a: "Starting 19 July 2026 we gave Claude Code (an AI coding agent) a standing brief to grow FrazzleCRM autonomously — no paid ads. It researches, writes marketing pages and free tools, opens pull requests, merges them, deploys to production and reports its own metrics on a schedule. This page shows the real numbers it has produced so far.",
  },
  {
    q: "How many pages has the AI agent shipped?",
    a: `As of ${METRICS.asOf}, ${METRICS.pagesIndexed} pages are indexed in the sitemap, including ${METRICS.freeTools} free tools, three honest competitor-comparison pages, six audience pages, an integrations hub with seven provider guides, an engineering blog, a ${METRICS.glossaryTerms}-term glossary, a git-generated changelog, and ${METRICS.templates} funnel templates — all built and deployed by the agent.`,
  },
  {
    q: "How many paying customers does FrazzleCRM have?",
    a: `Zero, as of ${METRICS.funnelAsOf}. We're publishing that on purpose. The tool works — ${METRICS.generatedMaps} funnel maps have been generated by visitors — but ${METRICS.claimedMaps} have been claimed into an account, none of them since 20 July, and there is no paying customer yet. The honest read is that the bottleneck is traffic, not the product. Honest metrics are more useful than a highlight reel, to us and to anyone deciding whether to try the tool.`,
  },
  {
    q: "Can an AI agent really run growth on its own?",
    a: "Partly. Claude Code handles the whole engineering-as-marketing loop — research, build, test, deploy, measure — without a human in the loop for each step. What it can't do is manufacture demand: distribution and a product people pay for still have to be real. This experiment is a live test of exactly where that line sits.",
  },
  {
    q: "How does FrazzleCRM's AI integration work?",
    a: "FrazzleCRM ships a built-in MCP (Model Context Protocol) server, so an AI assistant like Claude or ChatGPT can read and build your automations directly. The same capability the agent uses to grow the product is available to every Pro customer to run their own CRM. See the /ai page for the tool list.",
  },
];

function Stat({ value, label, dim }: { value: string; label: string; dim?: boolean }) {
  return (
    <div
      className={`rounded-2xl border p-5 text-center ${
        dim
          ? "border-slate-200 bg-slate-50"
          : "border-indigo-100 bg-indigo-50/40"
      }`}
    >
      <div
        className={`text-[30px] font-extrabold tracking-tight ${
          dim ? "text-slate-700" : "text-indigo-700"
        }`}
      >
        {value}
      </div>
      <div className="mt-1 text-[12.5px] font-medium leading-snug text-slate-500">
        {label}
      </div>
    </div>
  );
}

export default function BuildingInPublic() {
  return (
    <SiteShell
      title="We let an AI agent run our growth — the real numbers"
      description={`FrazzleCRM's build-in-public experiment: we pointed Claude Code at our own CRM and let it run growth autonomously. Real metrics as of ${METRICS.asOf} — ${METRICS.pagesIndexed} pages shipped, ${METRICS.generatedMaps} funnel maps generated, and yes, still 0 paying customers. Honest numbers, updated as they change.`}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-12 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Building in public
          </div>
          <h1 className="mx-auto max-w-3xl text-[34px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[44px]">
            We pointed an AI agent at our own CRM and let it run growth.
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-[15.5px] leading-relaxed text-slate-600">
            On 19 July 2026 we gave Claude Code a standing brief: grow
            FrazzleCRM, no paid ads. It researches, builds pages and free
            tools, ships pull requests, deploys to production, and reports its
            own numbers. This page is those numbers — the good ones and the
            uncomfortable ones. Last updated {METRICS.asOf}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <Stat value={`${METRICS.daysRunning}`} label="days running autonomously" />
          <Stat value={`${METRICS.pagesIndexed}`} label="pages shipped & indexed" />
          <Stat value={`${METRICS.freeTools}`} label="free tools built" />
          <Stat value={`${METRICS.templates}`} label="funnel templates" />
          <Stat
            value={`${METRICS.generatedMaps}`}
            label={`funnel maps generated by visitors (${METRICS.funnelAsOf})`}
          />
          <Stat
            value={`${METRICS.signups}`}
            label={`sign-ups (${METRICS.funnelAsOf})`}
            dim
          />
          <Stat
            value={`${METRICS.claimedMaps}`}
            label="maps claimed into accounts"
            dim
          />
          <Stat value={`$${METRICS.mrr}`} label="MRR — no paying customer yet" dim />
        </div>
        <p className="mt-4 text-center text-[12px] text-slate-400">
          Every figure is pulled from our production database and growth log.
          We update them as they change — including when they&apos;re
          unflattering. The product side of the same story is the{" "}
          <Link
            href="/changelog"
            className="font-semibold text-slate-500 underline hover:text-slate-700"
          >
            changelog
          </Link>
          , which is generated from git rather than written.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-8">
        <h2 className="text-[22px] font-bold text-slate-900">
          What the agent has shipped
        </h2>
        <p className="mt-2 max-w-2xl text-[14.5px] leading-relaxed text-slate-600">
          None of the pages below were written by a human. Each was researched,
          built, tested, and deployed by the agent — then wired into the site so
          it isn&apos;t an orphan.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {SHIPPED.map((s) => (
            <Link
              key={s.href}
              href={s.href}
              className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
            >
              <div className="text-[15px] font-bold text-slate-900 group-hover:text-indigo-700">
                {s.label} <span aria-hidden>→</span>
              </div>
              <div className="mt-1 text-[13.5px] leading-relaxed text-slate-500">
                {s.note}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <h2 className="text-[22px] font-bold text-slate-900">
            What&apos;s not working (yet)
          </h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">
            The honest part. Eighteen funnel maps have been generated by
            visitors, but <strong>zero</strong> have been claimed into an
            account, and there is <strong>no paying customer</strong>. The top
            of the funnel is alive; the conversion from &ldquo;that&apos;s a
            neat map&rdquo; to &ldquo;I&apos;ll build my business on this&rdquo;
            is not. One or two maps a day also isn&apos;t enough volume to learn
            much yet — distribution is the real bottleneck, not another landing
            page. That&apos;s the next thing the agent is working on, and this
            page will show whether it moves.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-12">
        <h2 className="text-[22px] font-bold text-slate-900">How it works</h2>
        <div className="mt-5 space-y-4 text-[14.5px] leading-relaxed text-slate-600">
          <p>
            The agent is{" "}
            <span className="font-semibold text-slate-800">Claude Code</span>,
            running on a schedule. Each run it reads a ranked backlog, picks the
            highest-value unshipped item, builds it in an isolated branch, runs
            the type-checker and a full production build, opens a pull request,
            merges it, and deploys the container to our Kubernetes cluster —
            then pings search engines so the new page gets indexed.
          </p>
          <p>
            It can do the &ldquo;build a CRM automation&rdquo; part because
            FrazzleCRM ships a built-in{" "}
            <Link href="/ai" className="font-medium text-indigo-600 hover:text-indigo-500">
              MCP server
            </Link>
            . That&apos;s not a growth hack reserved for us — every Pro customer
            gets the same thing: point Claude or ChatGPT at your workspace and
            it reads, drafts, and audits your funnels the same way the map on
            screen already does.
          </p>
          <p>
            The whole product is built on one idea: the{" "}
            <Link href="/templates" className="font-medium text-indigo-600 hover:text-indigo-500">
              funnel map you draw
            </Link>{" "}
            <em>is</em> the automation that runs. An AI agent can grow it
            precisely because the thing it&apos;s editing is legible — to
            humans and to models alike.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-4">
        <h2 className="text-[22px] font-bold text-slate-900">
          Questions people ask
        </h2>
        <div className="mt-5 space-y-5">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-[15px] font-semibold text-slate-900">
                {f.q}
              </h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-slate-600">
                {f.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-[26px] font-bold text-slate-900">
          Want to be the number that changes?
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] text-slate-600">
          The MRR counter above is at $0. Free forever tier — 5 flows, 1 map,
          500 contacts, no card. Clone a{" "}
          <Link href="/templates" className="font-medium text-indigo-600 hover:text-indigo-500">
            template
          </Link>{" "}
          and see your first funnel run in minutes.
        </p>
        <a
          href={`${APP_URL}/signup`}
          className="mt-6 inline-block rounded-xl bg-indigo-600 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
        >
          Start free
        </a>
      </section>
    </SiteShell>
  );
}
